<script lang="ts">
  import { onMount } from 'svelte';
  import { tweened } from 'svelte/motion';
  import { cubicOut } from 'svelte/easing';
  import { cn } from '@/lib/utils';
  import { round } from '@/Util/utils';

  // Inputs
  export let steepestSlope: number = 0; // percent grade
  export let kgeChangeWatts: number; // provided but NOT shown
  export let longestDistance: number = 0; // distance value
  export let distanceUnit: string = 'km'; // 'km' or 'mi'
  export let revealed: boolean = false;
  export let useMobileLayout: boolean;

  $: width = useMobileLayout ? 300 : 600;

  const tidy = (n: number) => new Intl.NumberFormat().format(Math.round(n ?? 0));

  // Rider progress along the road (0..1)
  const riderProgress = tweened(0, { duration: 1800, easing: cubicOut });
  // Count-up for slope and distance (distance is shown via odometer, but number is used for a11y)
  const slopeDisplay = tweened(0, { duration: 700, easing: cubicOut });
  const distanceDisplay = tweened(0, { duration: 800, easing: cubicOut });

  $: if (revealed) {
    riderProgress.set(1);
    slopeDisplay.set(steepestSlope ?? 0);
    distanceDisplay.set(longestDistance ?? 0);
  } else {
    riderProgress.set(0);
    slopeDisplay.set(0);
    distanceDisplay.set(0);
  }

  // SVG path ref and metrics
  let roadPath: SVGPathElement;
  let pathLen = 0;
  onMount(() => {
    if (roadPath) {
      pathLen = roadPath.getTotalLength?.() ?? 0;
    }
  });

  // Derived rider position along the path
  let riderX = 0, riderY = 0, riderAngle = 0;
  $: if (roadPath && pathLen > 0) {
    const t = Math.max(0, Math.min(1, $riderProgress));
    const at = roadPath.getPointAtLength(t * pathLen);
    riderX = at.x; riderY = at.y;
    // compute tangent angle
    const prev = roadPath.getPointAtLength(Math.max(0, t * pathLen - 1));
    riderAngle = Math.atan2(at.y - prev.y, at.x - prev.x) * 180 / Math.PI;
  }

  // Slope label position along the path (fixed sampling around mid steep segment)
  let labelX = 0, labelY = 0, labelAngle = 0;
  // Warning sign (first bump) position
  let warnX = 0, warnY = 0, warnAngle = 0;
  $: if (roadPath && pathLen > 0) {
    const tLab = 0.6; // mid section label (unused after replacing with warning sign)
    const p = roadPath.getPointAtLength(tLab * pathLen);
    const prev = roadPath.getPointAtLength(Math.max(0, tLab * pathLen - 1));
    labelX = p.x; labelY = p.y;
    labelAngle = Math.atan2(p.y - prev.y, p.x - prev.x) * 180 / Math.PI;

    // First bump approximate: early segment around 25%
    const tWarn = 0.45;
    const pw = roadPath.getPointAtLength(tWarn * pathLen);
    const pwPrev = roadPath.getPointAtLength(Math.max(0, tWarn * pathLen - 1));
    const theta = Math.atan2(pw.y - pwPrev.y, pw.x - pwPrev.x); // radians
    warnAngle = theta * 180 / Math.PI;
    // Offset normal to the path so the sign sits just above the road
    const nX = Math.sin(theta);
    const nY = -Math.cos(theta);
    warnX = pw.x + nX * 14;
    warnY = pw.y + nY * 14;
  }

  // Place small roadside markers along the path at fixed intervals
  const markerCount = 6;
  let markers: { x: number, y: number, r: number }[] = [];
  $: if (roadPath && pathLen > 0) {
    markers = Array.from({ length: markerCount }).map((_, i) => {
      const t = (i + 1) / (markerCount + 1);
      const p = roadPath.getPointAtLength(t * pathLen);
      const prev = roadPath.getPointAtLength(Math.max(0, t * pathLen - 1));
      const ang = Math.atan2(p.y - prev.y, p.x - prev.x) * 180 / Math.PI;
      return { x: p.x, y: p.y, r: ang };
    });
  }

  // Odometer helpers
  function getDigits(n: number): number[] {
    const v = Math.max(0, Math.round(n ?? 0));
    return v.toString().split('').map(d => parseInt(d));
  }
  $: distanceDigits = getDigits($distanceDisplay);

  // Silence unused export warning (kgeChangeWatts is intentionally unused)
  $: void kgeChangeWatts;
  $: void steepestSlope;
  $: void longestDistance;
  $: void revealed;

</script>

<div class={cn(`terrain-card ${revealed ? 'revealed' : ''}`, useMobileLayout ? 'w-[300px]' : 'w-[600px]')}
     aria-label={`Terrain highlights: steepest ${Math.round(steepestSlope ?? 0)}% grade, longest distance ${tidy(longestDistance)} ${distanceUnit}`}>
  <!-- Canyon backdrop: layered cliffs and sky -->
  <div class="sky" aria-hidden="true"></div>
  <div class="cliff layer1" aria-hidden="true"></div>
  <div class="cliff layer2" aria-hidden="true"></div>
  <div class="cliff layer3" aria-hidden="true"></div>
  <div class="clouds" aria-hidden="true"></div>

  <!-- Road SVG and roadside markers -->
  <svg class="scene" viewBox="0 0 {width} 360" width={width} height="360" aria-hidden="true">
    <!-- Winding road -->
    <path
      bind:this={roadPath}
      class="road"
      d={useMobileLayout ? "M 10 320 C 40 260, 70 250, 100 260 C 140 275, 160 220, 180 190 C 205 150, 230 150, 260 170" : "M 20 320 C 80 260, 140 250, 200 260 C 280 275, 320 220, 360 190 C 410 150, 460 150, 520 170"}
      />

    <!-- Roadside markers -->
    {#if markers.length}
      {#each markers as m}
        <rect class="marker" x={m.x - 2} y={m.y - 12} width="4" height="10" transform={`rotate(${m.r} ${m.x} ${m.y})`} />
      {/each}
    {/if}

    <!-- Rider sprite (small circle + bike icon) -->
    <g class="rider" transform={`translate(${riderX}, ${riderY}) rotate(${riderAngle})`}>
      <circle r="7" class="rider-dot" />
      <g transform="translate(0,-12)">
        <text class="rider-icon" text-anchor="middle" dominant-baseline="middle" transform="scale(-1,1)">🚴</text>
      </g>
    </g>

  <!-- Highway sign: show longest ride distance -->
    <g class="highway-sign">
      <g class="board-in">
        <rect x={useMobileLayout ? "175" : "395"} y="100" width="90" height="48" rx="6" ry="6" class="board" />
        <text x={useMobileLayout ? "220" : "440"} y="118" text-anchor="middle" class="board-title">Longest Ride</text>
        <text x={useMobileLayout ? "220" : "440"} y="138" text-anchor="middle" class="board-value">{tidy($distanceDisplay)} {distanceUnit}</text>
      </g>
      <rect x={useMobileLayout ? "220" : "440"} y="148" width="4" height="24" class="post" />
    </g>

    <!-- Yellow warning road sign on first bump: shows steepest ride -->
    {#if pathLen > 0}
      <g class="warn-sign" transform={`translate(${warnX}, ${warnY}) rotate(${warnAngle})`}>
        <g class="warn-tilt">
          <!-- Yellow diamond with percent only -->
          <rect x="-50" y="-50" width="40" height="40" transform="rotate(45)" rx="4" ry="4" class="warn-board" />
          <text x="0" y="-37" text-anchor="middle" class="warn-value">{round($slopeDisplay, 1)}%</text>
          <!-- Yellow rectangular plate below with label -->
          <rect x="-40" y="-15" width="80" height="16" rx="3" ry="3" class="warn-plate" />
          <text x="0" y="-3" text-anchor="middle" class="warn-plate-text">Steepest Ride</text>
        </g>
        <rect x="-2" y="0" width="4" height="12" class="warn-post" />
      </g>
    {/if}
  </svg>

  <!-- Odometer at the bottom -->
  <!-- <div class="odometer">
    <div class="windows">
      {#each distanceDigits as d, i}
        <div class="column" style={`--i:${i}; --d:${d}`}> 
          <div class="reel {revealed ? 'roll' : ''}">
            {#each Array.from({length: 10}) as _, n}
              <div class="digit">{n}</div>
            {/each}
          </div>
        </div>
      {/each}
    </div>
    <div class="unit">{distanceUnit}</div>
    <div class="caption">Longest distance</div>
  </div> -->
</div>

<style>
  .terrain-card {
    max-height: 500px;
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    box-shadow: 0 12px 24px rgba(0,0,0,0.35);
    color: #f3f6fb;
    background: linear-gradient(180deg, #1c1220, #0d0a12);
  }
  .terrain-card .sky {
    position: absolute; inset: 0;
    background: radial-gradient(120% 80% at 50% -10%, #ffcf9c 0%, rgba(255,159,80,0.9) 30%, rgba(129,73,165,0.85) 60%, rgba(15,10,20,1) 100%);
    opacity: 0; animation: skyIn 220ms ease-out forwards;
  }
  .revealed .sky { animation-delay: 0ms; }
  @keyframes skyIn { to { opacity: 1; } }

  /* Layered canyon cliffs */
  .cliff { position: absolute; left: 0; right: 0; height: 80%; opacity: 0; filter: blur(0.2px); }
  .layer1 { bottom: -40%; background: linear-gradient(180deg, rgba(145,78,44,0.7), rgba(90,45,25,0.7)); clip-path: polygon(0% 20%, 15% 15%, 30% 19%, 45% 14%, 60% 18%, 75% 12%, 90% 15%, 100% 11%, 100% 100%, 0% 100%); animation: cliffIn 260ms 40ms ease-out forwards; }
  .layer2 { bottom: -22%; background: linear-gradient(180deg, rgba(176,100,60,0.6), rgba(120,60,35,0.6)); clip-path: polygon(0% 25%, 20% 20%, 35% 24%, 55% 18%, 75% 22%, 90% 17%, 100% 19%, 100% 100%, 0% 100%); animation: cliffIn 260ms 80ms ease-out forwards; }
  .layer3 { bottom: -8%; background: linear-gradient(180deg, rgba(210,140,90,0.65), rgba(140,80,50,0.65)); clip-path: polygon(0% 20%, 20% 15%, 40% 19%, 60% 14%, 80% 18%, 100% 13%, 100% 100%, 0% 100%); animation: cliffIn 260ms 120ms ease-out forwards; }
  @keyframes cliffIn { to { opacity: 1; } }

  .clouds { position: absolute; inset: 0; background: radial-gradient(60% 30% at 30% 20%, rgba(255,255,255,0.06), transparent 60%), radial-gradient(50% 30% at 70% 25%, rgba(255,255,255,0.05), transparent 70%); opacity: 0; animation: cloudsIn 220ms 60ms ease-out forwards, drift 12s 1.5s linear infinite; pointer-events: none; }
  @keyframes cloudsIn { to { opacity: 1; } }
  @keyframes drift { 0% { transform: translateX(0);} 50% { transform: translateX(-8px);} 100% { transform: translateX(0);} }

  .scene { position: relative; z-index: 2; display: block; margin-top: 8px; overflow: visible; }
  .road { fill: none; stroke: #f6f7fb; stroke-width: 6; stroke-linecap: round; stroke-linejoin: round; filter: drop-shadow(0 2px 6px rgba(0,0,0,0.35)); stroke-dasharray: 600; stroke-dashoffset: 600; }
  .revealed .road { animation: drawRoad 2800ms 160ms cubic-bezier(0.22,0.72,0.25,1) forwards; }
  @keyframes drawRoad { to { stroke-dashoffset: 0; } }

  .marker { fill: #ffd074; opacity: 0; }
  .revealed .marker { animation: markerPop 960ms 880ms ease-out forwards; }
  @keyframes markerPop { 0% { opacity: 0; transform: scale(0.6);} 100% { opacity: 1; transform: scale(1);} }

  .rider .rider-dot { fill: #ffefcf; stroke: rgba(0,0,0,0.25); stroke-width: 1; filter: drop-shadow(0 0 6px rgba(255,239,207,0.55)); }
  .rider .rider-icon { font-size: 26px; }
  .rider { opacity: 0; }
  .revealed .rider { animation: riderIn 880ms 200ms ease-out forwards; }
  @keyframes riderIn { to { opacity: 1; } }

  /* Highway sign styling */
  .highway-sign .board { fill: #1f6d3e; stroke: #e8f5e9; stroke-width: 2; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.35)); }
  .highway-sign .board-title { font: 700 12px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif; fill: #e8f5e9; letter-spacing: 0.3px; }
  .highway-sign .board-value { font: 800 14px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif; fill: #ffffff; }
  .highway-sign .post { fill: #6d5a43; }
  .highway-sign .board-in { transform-origin: 220px 124px; transform-box: fill-box; opacity: 0; }
  .revealed .highway-sign .board-in { animation: signIn 1040ms 1500ms cubic-bezier(0.22,0.72,0.25,1) forwards; }
  @keyframes signIn { 0% { opacity: 0; transform: translateY(8px) scale(0.96);} 60% { opacity: 1; transform: translateY(0) scale(1.02);} 100% { opacity: 1; transform: translateY(0) scale(1);} }

  /* Yellow warning sign styling */
  .warn-sign .warn-board { fill: #ffd35a; stroke: #7a4e00; stroke-width: 2; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.35)); }
  .warn-sign .warn-post { fill: #6d5a43; }
  .warn-sign .warn-value { font: 800 12px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif; fill: #2a1a00; }
  .warn-sign .warn-plate { fill: #ffd35a; stroke: #7a4e00; stroke-width: 2; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.35)); }
  .warn-sign .warn-plate-text { font: 700 10px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif; fill: #2a1a00; letter-spacing: 0.2px; }
  .warn-sign .warn-tilt { opacity: 0; transform-origin: 0 0; }
  .revealed .warn-sign .warn-tilt { animation: warnIn 1200ms 1000ms cubic-bezier(0.22,0.72,0.25,1) forwards; }
  @keyframes warnIn { 0% { opacity: 0; transform: translateY(6px) rotate(-10deg) scale(0.96);} 60% { opacity: 1; transform: translateY(0) rotate(6deg) scale(1.02);} 100% { opacity: 1; transform: translateY(0) rotate(0) scale(1);} }

  /* Odometer styling */
  /* .odometer { position: absolute; left: 0; right: 0; bottom: 10px; margin: 0 auto; width: 88%; background: linear-gradient(180deg, rgba(18,18,24,0.85), rgba(8,8,12,0.95)); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 8px 10px 10px; box-shadow: 0 8px 18px rgba(0,0,0,0.35); opacity: 0; transform: translateY(12px); }
  .revealed .odometer { animation: odoIn 380ms 420ms ease-out forwards; }
  @keyframes odoIn { to { opacity: 1; transform: translateY(0); } }
  .odometer .windows { display: flex; gap: 4px; justify-content: center; align-items: center; }
  .odometer .column { position: relative; width: 18px; height: 24px; overflow: hidden; background: linear-gradient(180deg, #16161c, #0c0c10); border-radius: 4px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.06); }
  .odometer .reel { position: absolute; top: 0; left: 0; right: 0; display: flex; flex-direction: column; transition: transform 700ms cubic-bezier(0.22,0.72,0.25,1); transform: translateY(0); }
  .revealed .odometer .reel.roll { transform: translateY(calc(var(--d, 0) * -24px)); transition-delay: calc(0.12s + var(--i) * 0.05s); }
  .odometer .digit { height: 24px; display: grid; place-items: center; color: #e9edf6; font-weight: 800; font-size: 14px; letter-spacing: 0.4px; }
  .odometer .unit { text-align: center; font-size: 11px; opacity: 0.85; margin-top: 6px; }
  .odometer .caption { text-align: center; font-size: 10px; opacity: 0.7; margin-top: 2px; } */
</style>
